import { useTranslation } from 'react-i18next'

import type { PlanCapacity } from '@/types/planning'

/**
 * Capacity against commitment, for one period.
 *
 * The requirement is that the overflow is on the screen *before* the plan is
 * committed, not after the quarter is lost — so this sits above the commitment
 * board rather than in a report.
 */
export function CapacityPanel({ capacity }: { capacity: PlanCapacity }) {
  const { t } = useTranslation()

  const ceiling = Math.max(capacity.capacityDays ?? 0, capacity.committedDays, 1)
  const committedWidth = (capacity.committedDays / ceiling) * 100
  const capacityWidth = ((capacity.capacityDays ?? 0) / ceiling) * 100

  return (
    <section className="border-line bg-surface mb-6 rounded-md border p-4">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h3 className="text-ink-900 text-sm font-semibold">{t('planning.capacity')}</h3>
        <p className="text-ink-500 text-xs">
          {t('planning.capacityExplainer', {
            headcount: capacity.headcount,
            workingDays: capacity.workingDays,
          })}
        </p>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Figure label={t('planning.capacityDays')} value={capacity.capacityDays} />
        <Figure label={t('planning.committedDays')} value={capacity.committedDays} />
        <Figure
          label={t('planning.overflow')}
          value={capacity.overflowDays}
          tone={capacity.isOverCapacity ? 'blocked' : 'muted'}
        />
      </div>

      <div
        className="bg-surface-2 relative mt-4 h-3 overflow-hidden rounded-full"
        hidden={!capacity.isKnown}
      >
        <span
          className="absolute inset-y-0 start-0 rounded-full"
          style={{
            width: `${Math.min(100, committedWidth)}%`,
            backgroundColor: capacity.isOverCapacity
              ? 'var(--color-state-blocked)'
              : 'var(--color-brand-600)',
          }}
        />
        {/* Where capacity runs out, so the overflow is visible as a distance
            rather than as a number to work out. */}
        <span
          aria-hidden="true"
          className="bg-ink-900 absolute inset-y-0 w-px"
          style={{ insetInlineStart: `${Math.min(100, capacityWidth)}%` }}
        />
      </div>

      <p
        className={`mt-3 text-sm ${
          capacity.isOverCapacity ? 'text-[color:var(--color-state-blocked)]' : 'text-ink-700'
        }`}
      >
        {!capacity.isKnown
          ? // Nobody is recorded in this division yet, so there is no honest
            // number to put here. Saying so beats reporting zero and calling
            // every plan over budget.
            t('planning.capacityUnknown')
          : capacity.isOverCapacity
            ? t('planning.overflowWarning', { days: capacity.overflowDays })
            : t('planning.withinCapacity')}
      </p>

      {capacity.holidays.length > 0 ? (
        <details className="mt-3">
          <summary className="text-ink-500 cursor-pointer font-mono text-[10px] tracking-widest uppercase">
            {t('planning.holidays')} ({capacity.holidays.length})
          </summary>
          <ul className="text-ink-700 mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {capacity.holidays.map((holiday) => (
              <li key={`${holiday.date}-${holiday.name}`}>
                <span className="font-mono tabular-nums">{holiday.date}</span> {holiday.name}
                {holiday.isEstimated ? (
                  <span className="text-ink-500"> — {t('planning.estimated')}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  )
}

function Figure({
  label,
  value,
  tone = 'normal',
}: {
  label: string
  value: number | null
  tone?: 'normal' | 'blocked' | 'muted'
}) {
  const { t } = useTranslation()

  const color =
    tone === 'blocked'
      ? 'text-[color:var(--color-state-blocked)]'
      : tone === 'muted'
        ? 'text-ink-500'
        : 'text-ink-900'

  return (
    <div>
      <div className={`text-2xl font-semibold tabular-nums ${color}`}>
        {value === null ? (
          <span className="text-ink-500">—</span>
        ) : (
          <>
            {value}
            <span className="text-ink-500 ms-1 text-xs font-normal">{t('planning.days')}</span>
          </>
        )}
      </div>
      <div className="text-ink-500 mt-0.5 font-mono text-[10px] tracking-widest uppercase">
        {label}
      </div>
    </div>
  )
}
